import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
  StyledBudgetsActivePageContainer,
  StyledBudgetsActivePageContent,
  StyledBudgetsActivePageTitle
} from './StyledBudgetsActivePage';
import EmptyOrTable from '../../components/EmptyOrTable';
import PageContainer from '../../components/PageContainer';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import OverflowProgress from '../../components/OverflowProgress';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import TableFilters from '../../components/pages/BudgetRequestsPage/TableFilters';

import ExpenseAgentChat from '../../components/pages/OverviewPage/AgentChatPanel';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import LoadMoreButton from '../../components/pages/TransactionsPage/LoadMoreButton';
import IconWrapper from '../../components/IconWrapper';
import {useTabState} from '../../hooks';
import {
  amountHelpers,
  budgetHelpers,
  objectHelpers,
  tabHelpers,
  tableHelpers,
  transactionsHelpers
} from '../../utils/helpers';
import {alertActions} from '../../state/actions/alert.actions';
import {budgetActions} from '../../state/actions/budget.actions';
import routes from '../../routes/routes.json';
import {ChevronDownIcon} from '../../icons';
import {colorsStyles} from '../../utils/styles';
import {systemHashKeysConstants} from '../../constants';

const columnProps = { width: 'calc(100% / 6)' };

const view = 'BudgetActive';

const requestedBudgetStatuses = budgetHelpers.getActiveBudgetStatuses();

const BudgetsActivePage = ({ ...props }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState({expanded: [], closed: []});
  const [visibleData, setVisibleData] = useState([]);
  const [enabledWebSocketConnection, setEnabledWebSocketConnection] = useState(false);

  const [t] = useTranslation('main');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const isOpenAgentChat = useSelector(state => state?.agent?.agentChatProps?.open || false);

  const budgets = useSelector(state => state.budget?.budgets || []);

  const title = t('pageTitles.budgetsActive');

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
  } = useTabState({defaultQuery: { order_by: '-budget_id' }, tabKeys: [view]});

  const tableRows = useMemo(() => {
    return budgetHelpers.getGroupedActiveBudgets({rows: tableData[view]?.rows, budgets });
  }, [tableData[view]?.rows, budgets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const query = objectHelpers.removeKeysFromObject(
      objectHelpers.getSearchParams(searchParams)
    );
    loadData({ initial: true, query });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpenAgentChat && !enabledWebSocketConnection) {
      setEnabledWebSocketConnection(true);
    }
  }, [isOpenAgentChat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const visibleRows = tableRows || [];
    const groupedIds = visibleRows.filter(row => row.isGrouped).map(row => row.id);

    setExpandedRowKeys(prev => {
      const prevState = prev || { expanded: [], closed: [] };
      const newClosed = prevState.closed.filter(id => groupedIds.includes(id));
      const newState = { expanded: groupedIds, closed: newClosed };

      const invisibleRowsIds = visibleRows
        .filter(row => newClosed.includes(row.id))
        .map(row => (row.expanded_ids || []))
        .flat();

      setVisibleData(visibleRows.filter(row => !invisibleRowsIds.includes(row.id)));

      return newState;
    });
    }, [tableRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishTableLoading = () => {
    setLoading(prevState => ({...prevState, [view]: false}));
  }

  const loadData = ({
    query = null,
    initial = false,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    setLoading(prevState => ({...prevState, [view]: true}));

    // add active statuses if not present in the query
    let requestQuery = { ...viewQuery };
    if (!query?.status?.length) requestQuery = { ...requestQuery, status: requestedBudgetStatuses };

    dispatch(
      budgetActions.getBudgetRequests({
        query: requestQuery,
        successCallback: (response) => {
          response.results = budgetHelpers.getGroupedBudgetsByBudget(response.results);

          if (!initial) {
            const combinedRows = [...tableData[view].rows, ...response.results].filter(row => !row.isGrouped);
            response.results = budgetHelpers.getGroupedBudgetsByBudget(combinedRows);
          }

          tabHelpers.handleStoreTableDataWithPrev({
            tableData: initial ? tableData : { ...tableData, [view]: { ...tableData[view], rows: [] } },
            setTableData,
            view,
            response,
            initial,
            updateCurrentPage
          });

          finishTableLoading();
          onSuccess?.();
        },
        errorCallback: finishTableLoading
      })
    );
  }

  const renderAmount = (value) => (
    <div className={`amount ${value < 0 ? 'negative' : ''}`}>
      {amountHelpers.getAmountWithCurrencyCode(value)}
    </div>
  );

  const columns = [
    {
      ...columnProps,
      className: 'no-wrap',
      dataIndex: 'name',
      render: (_, budget) => {
        const name = budgetHelpers.getBudgetName(budget);
        return budget?.isGrouped ? <span className='fw-500'>{name}</span> : name;
      },
      title: t('name'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      render: budgetHelpers.getOwner,
      width: '80px',
      title: t('Owner'),
    },
    {
      ...columnProps,
      dataIndex: 'utilization_percentage',
      title: t('utilization'),
      render: (value) => {
        const percent = (value || 0).toFixed(0);
        return <OverflowProgress percent={percent} />;
      }
    },
    {
      ...columnProps,
      dataIndex: 'limit_amount',
      title: `${t('Budget')} ${t('limit')}`,
      render: renderAmount
    },
    {
      ...columnProps,
      dataIndex: 'available_amount',
      title: t('available'),
      render: renderAmount
    },
    {
      ...columnProps,
      dataIndex: 'status',
      title: t('status'),
      render: (_, record) => {
        if (record.isGrouped) return;
        return budgetHelpers.getBudgetStatusMark(record);
      }
    },
  ];

  const handleOnRowClick = (e, record) => {
    if (record.isGrouped) return;
    navigate(`${routes.budgetsActive}/${record.id}`);
  }

  const handleOnFilter = (filterQuery) => {
    tabHelpers.handleOnFilter({view, filterQuery, loadDataFunc: loadData, queries, setQueries});
    setSearchParams(
      objectHelpers.getSearchQueryParams({searchParams, filterQuery})
    );
  }

  const handleOnTableChange = (pagination, filters, sorter, extra) => {
    tabHelpers.handleOnTableChange({view, sorter, extra, loadDataFunc: loadData});
  }

  const onExpand = (expanded, record) => {
    if (!record.isGrouped) return;

    const expendedIds = record.expanded_ids || [];

    if (expanded) {
      const visibleDataIds = [...visibleData.map(row => row.id), ...expendedIds];
      setVisibleData(
        tableRows.filter(row => visibleDataIds.includes(row.id))
      );
      setExpandedRowKeys(state => ({
        ...state,
        expanded: [...state.expanded, record.id],
        closed: state.closed.filter(id => id !== record.id)
      }));
    } else {
      setVisibleData(
        visibleData.filter(row => !expendedIds.includes(row.id))
      );
      setExpandedRowKeys(state => ({
        ...state,
        expanded: state.expanded.filter(id => id !== record.id),
        closed: [...state.closed, record.id]
      }));
    }
  }

  const handleCloseAgent = (props) => {
    if (props?.confirmed) {
      alertActions.success(t('budgets:alerts.budgetRequestSubmittedSuccessWithRedirect'), 6000, 450);
      navigate(`${routes.budgetsRequests}${systemHashKeysConstants.ENABLE_WS}`);
    }
  }

  const tableProps = {
    expandable: {
      defaultExpandAllRows: true,
      expandedRowKeys: expandedRowKeys.expanded,
      expandedRowClassName: 'd-none',
      expandRowByClick: true,
      expandIcon: (props) => {
        if (!props.expandable) return null;
        return (
          <IconWrapper
            className={`expand-icon ${props.expanded ? 'expanded' : ''}`}
            color={colorsStyles.neutral_10}
            onClick={() => onExpand(!props.expanded, props.record)}
            size={16}
          >
            <ChevronDownIcon />
          </IconWrapper>
        )
      },
      onExpand,
      expandedRowRender: () => null, // no sub-content, just triggers our logic
      rowExpandable: record => record.isGrouped,
    },
    onChange: (pagination, filters, sorter, extra) => handleOnTableChange(pagination, filters, sorter, extra),
    rowClassName: (record) => transactionsHelpers.getRowClassName({
      record,
      groupedPropName: 'isGrouped'
    }),
    onRow: (record, index) => ({
      onClick: (e) => handleOnRowClick(e, record, index),
    }),
    pagination: false,
  }

  const loadMoreProps = tableHelpers.getLoadMoreProps({tableData, view, loadData});

  return (
    <PageContainer {...props}>
      <PageDocumentDetails title={title} />
      <BalancePageHeader breadcrumbs={[{ title: t('budgets') }, { title: t('active') }]} />
      <DashboardPageTransition>
        <StyledBudgetsActivePageContainer>
          <StyledBudgetsActivePageTitle>
            <h4>{t('budgets')} & {t('POs')}</h4>
            {/*<h5>(Jan - Dec ‘25)</h5>*/}
            {/*<InfoCircleOutlined style={{color: colorsStyles.neutral_7, fontSize: 14}} />*/}
          </StyledBudgetsActivePageTitle>
          <StyledBudgetsActivePageContent size='large' direction='vertical'>
            <TableFilters
              defaultQuery={queries[view]}
              filterFields={['search', 'status', 'owner']}
              exportAdditionalFields={{status: requestedBudgetStatuses}}
              onFilter={handleOnFilter}
              tableType='active'
            />
            <EmptyOrTable
              data={visibleData}
              loading={loading?.[view]}
              query={queries?.[view]}
              columns={columns}
              tableProps={tableProps}
            />
            <LoadMoreButton
              {...loadMoreProps}
              loading={loading?.[view] || loadMoreProps.loading}
              size='large'
            >
              {t('showMore')}
            </LoadMoreButton>
          </StyledBudgetsActivePageContent>
        </StyledBudgetsActivePageContainer>
      </DashboardPageTransition>

      <ExpenseAgentChat onClose={handleCloseAgent} />

    </PageContainer>
  );
}

export default BudgetsActivePage;
